import { api } from './client';

/**
 * The chat thread on a booking - `GET/POST /bookings/:id/messages`.
 *
 * ---------------------------------------------------------------------------
 * WHY A BOOKING, NOT A THREAD
 * ---------------------------------------------------------------------------
 * There is no separate conversation id: the booking itself is the thread key,
 * one per booking, between the customer and whichever professional is
 * assigned to it. Neither side ever sees the other's phone number - this is
 * the whole reason the thread exists rather than exchanging numbers.
 *
 * ---------------------------------------------------------------------------
 * WRITES CLOSE, READS DO NOT
 * ---------------------------------------------------------------------------
 * The backend closes `POST` a configurable stretch after the job completes
 * (and immediately on cancellation), but `GET` never closes - the thread is
 * kept as dispute evidence. A `POST` past that window comes back as a 409,
 * which the caller should read as "this conversation is closed", not as a
 * generic failure.
 */

/** One message, as the server sends and stores it. */
export type ChatMessageRow = {
  id: string;
  bookingId: string;
  senderType: 'customer' | 'pro';
  senderId: string;
  body: string;
  attachmentUrl: string | null;
  sentAt: string;
  readAt: string | null;
};

/** The whole thread for one booking, oldest first - exactly as the server returns it. */
export const fetchMessages = (
  bookingId: string,
  signal?: AbortSignal,
): Promise<ChatMessageRow[]> =>
  api.get<ChatMessageRow[]>(`/bookings/${bookingId}/messages`, { signal });

/**
 * Send one message. 1–2000 characters, enforced server-side too.
 *
 * Throws (409) once the write window has closed - see the file note. The
 * caller should show that as "you can no longer message this professional"
 * rather than a generic send failure.
 */
export const sendMessage = (
  bookingId: string,
  body: string,
): Promise<ChatMessageRow> =>
  api.post<ChatMessageRow>(`/bookings/${bookingId}/messages`, { body });
